// Positional cast restrictions ("Must be cast to ..."). Pure data + a
// predicate. Some cards may only be played to a specific board position.
// per Sorcery these are HARD restrictions (the card can't legally be played
// elsewhere), unlike the advisory placement gates. The position keys map to
// positional.cellMatchesPosition (back_row / corner / outer_column / ...).
//
// Keyed by card NAME (the catalog name), mirroring the avatar-rules pattern,
// so adding a card is a one-line data edit here. No schema plumbing.

import { cellMatchesPosition } from './positional';

const CAST_POSITION_BY_NAME = {
  'The Round Table': 'back_row',     // "Must be cast to your back row."
  Cornerstone: 'corner',             // "You may play this site to any corner."
  'The Ninth Legion': 'corner',      // "Must be cast to a corner."
  'Dormant Monstrosity': 'corner',   // "Must be cast to a corner void."
  Forsaken: 'outer_column',          // "Must be cast to an outer column."
};

/**
 * The required cast position for a card, or null if unrestricted.
 * @param {string} cardName  the catalog name.
 * @returns {('back_row'|'corner'|'outer_column'|'outer_row'|'edge'|'front_row')|null}
 */
export function castPositionFor(cardName) {
  return CAST_POSITION_BY_NAME[cardName] || null;
}

/**
 * Is `cardName` legal to cast on `cell` from `controllerRotated`'s
 * perspective? Unrestricted cards are always legal. A restricted card with
 * no cell/grid (or a non-matching cell) is illegal.
 */
export function isCastPositionLegal(cardName, cell, grid, controllerRotated) {
  const pos = castPositionFor(cardName);
  if (!pos) return true;
  if (!cell || !grid) return false;
  return cellMatchesPosition(pos, cell, grid, controllerRotated);
}

/**
 * The set of legal "col,row" keys for a position-restricted card, for the
 * drag-reach halo (cells in the set render legal; the rest red). Returns
 * null for an unrestricted card (no positional halo).
 */
export function legalCastCells(cardName, grid, controllerRotated) {
  const pos = castPositionFor(cardName);
  if (!pos || !grid) return null;
  const cells = new Set();
  for (let c = 0; c < grid.cols; c++) {
    for (let r = 0; r < grid.rows; r++) {
      if (cellMatchesPosition(pos, { col: c, row: r }, grid, controllerRotated)) {
        cells.add(`${c},${r}`);
      }
    }
  }
  return cells;
}

// ── Owner-relative summon restriction ────────────────────────────────────
// Some "Only It Can Cast" owner permanents restrict WHERE their owner-locked
// minions may be summoned, relative to the OWNER unit (not a fixed board
// cell). The Omphalos quartet: "Minions it casts must be summoned here" → the
// minion must land on the Omphalos's own cell. Keyed by the OWNER's name.
const SUMMON_AT_OWNER_BY_NAME = {
  'Algor Omphalos': 'here',
  'Char Omphalos': 'here',
  'Dank Omphalos': 'here',
  'Torrid Omphalos': 'here',
};

/** The owner-relative summon restriction ('here' | null) for an owner name. */
export function ownerSummonRestriction(ownerName) {
  return SUMMON_AT_OWNER_BY_NAME[ownerName] || null;
}

/**
 * The set of legal "col,row" keys for an owner-locked minion, relative to the
 * owner's cell. 'here' → just the owner's own cell. Returns null when the
 * owner has no summon restriction (the minion summons anywhere legal).
 */
export function legalOwnerSummonCells(ownerName, ownerCell) {
  const r = ownerSummonRestriction(ownerName);
  if (!r || !ownerCell) return null;
  if (r === 'here') return new Set([`${ownerCell.col},${ownerCell.row}`]);
  return null;
}

// Catalog entry restrictions apply to movement AND direct summons (Codex
// Summon). Like castRestrictions, this registry describes hard placement
// constraints rather than a triggered effect.
import { effectivePower } from './effectiveStats';
import { isCardSilenced } from './keywordQuery';

const MAX_ENTRY_POWER = { 'Gnome Hollows': 2 };

export function canEnterSite(unit, site) {
  const limit = MAX_ENTRY_POWER[site?.name];
  return limit == null || isCardSilenced(site) || effectivePower(unit) <= limit;
}
